<template>
  <v-container class='kim-background'>
    <h1 class='headline'>{{ $i18n.t('NotesPage.title')}}</h1>
    <p>{{getExercise.name}} {{ getTimeUntil }}</p>
    <v-row class='text-center'/>
    <v-row v-if='exerciseDocumentsLoaded'>
      <v-col cols='12'>
        <div class='subtitle-1 mb-2'>
          <v-icon class='pr-2 pb-1'>mdi-book-open-variant</v-icon>
          {{ $i18n.t('NotesPage.exerciseDocuments') }}
        </div>
        <v-expansion-panels accordion='accordion'>
          <v-expansion-panel
            v-for='(item) in currentExerciseDocuments'
            :key="`exercise_${item.documentName}`"
          >
            <v-expansion-panel-header><div><v-icon class="pr-2 primary--text">mdi-file-pdf</v-icon>{{item.documentName}}</div></v-expansion-panel-header>
            <v-expansion-panel-content style="height: 66vh">
              <Pdf :pdf-url="item.documentUrl" :use-case='"candidate"' />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <v-row align='center' class='mt-5'>
      <v-col class='col-12 col-lg-8' cols='8'>
        <v-row>
          <v-col cols='10' v-if='currentNote != null'>
            <v-text-field :label="$i18n.t('NotesPage.labelNotesTitle')" v-model='currentNote.name' @change='onTitleChange'/>
          </v-col>
          <v-col cols='2' v-if='currentNote != null'>
            <v-radio-group :value='currentNote.penInkFormat' @change='penInkFormatChanged' :mandatory='false'>
              <v-radio :label="$i18n.t('NotesPage.html')" value='html'/>
              <v-radio :label="$i18n.t('NotesPage.webInk')" value='webInk'/>
            </v-radio-group>
          </v-col>
          <v-col cols='12' v-if='currentNote !== null'>
            <TinyMCE v-model='currentNote' v-if="currentNote.penInkFormat === 'html'" @textChanged='onTextChange'/>
            <InkNote v-model='currentNote' v-if="currentNote.penInkFormat === 'webInk'" @onChanged='onTextChange'/>
          </v-col>
          <v-col cols='12'>
            <v-list elevation='2' light>
              <template v-for='(item, i) in getCurrentNotes()'>
                <v-list-item v-if='item.deleted !== true' :key='`${i}_${item.modified}`' @click='setNote(item)' :class='{isHighlighted: item === currentNote}'>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }} {{$i18n.t('NotesPage.page') }} {{item.page}}</v-list-item-title>
                    <v-list-item-subtitle v-if='item.id && item.modified !== true'>{{ $i18n.t('NotesPage.statusSync') }}</v-list-item-subtitle>
                    <v-list-item-subtitle v-else>{{ $i18n.t('NotesPage.statusModified') }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon @click='confirmDeleteNote(item)'>
                      <v-icon color='lighten-1'>mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-list>
          </v-col>
          <v-col cols='12'>
<!--            <v-btn @click='persistNote()' class='mr-2 mb-2'><v-icon color='lighten-1' class='pr-2'>mdi-sync</v-icon>{{ $i18n.t('NotesPage.save') }}</v-btn>-->
<!--            <v-btn @click='displayManual = !displayManual' class='mr-2 mb-2'><v-icon color='lighten-1' class='pr-2'>mdi-book-open-page-variant</v-icon>{{ $i18n.t('NotesPage.showManual') }}</v-btn>-->
            <v-btn @click='addNewNote' class='mr-2 mb-2'><v-icon color='lighten-1' class='pr-2'>mdi-plus</v-icon>{{ $i18n.t('NotesPage.newPage') }}</v-btn>
          </v-col>
        </v-row>
      </v-col>
      <!-- currently no note information -->
<!--      <v-col cols='4' align-self='start' class='pl-5 col-12 col-lg-4 mb-10'>-->
<!--        <notes-information :information='getExercise().informations'/>-->
<!--      </v-col>-->
    </v-row>
    <modal-confirm-dialog
      :show-dialog='confirmDialog.show'
      :item='confirmDialog.item'
      :title="$i18n.t('NotesPage.switchTypeTitle')"
      :question="$i18n.t('NotesPage.switchTypeQuestion')"
      @confirm='dialogConfirmed(confirmDialog.item)'
      @close='confirmDialog.show=false'/>
    <modal-confirm-delete
      :show-dialog='deleteDialog.show'
      :item='deleteDialog.item'
      :question='deleteDialog.question'
      @confirm='deleteNoteConfirmed(deleteDialog.item)'
      @close='deleteDialog.show = false'/>
    <modal-exercise-blocker v-if='isExerciseBlocked(currentExerciseId)'
      :show-dialog='isExerciseBlocked(currentExerciseId)'
      @reloadInfo='$emit("reloadInfo")'
      @backToOverview='$emit("returnToOverview")'
    />
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import dayjs from 'dayjs';
import BusinessLogicExercise from '@/libs/BusinessLogicExercise';
import ModalConfirmDialog from '@/components/ModalConfirmDialog';
import Pdf from '@/components/Pdf';
import TinyMCE from '@/components/TinyMCE';
import ModalConfirmDelete from '@/components/ModalConfirmDelete';
import InkNote from '@/components/InkNote';
import ACTIONS from '@/store/storeActions';
import LoadingState from '@/api/LoadingState';
import ModalExerciseBlocker from '@/components/ModalExerciseBlocker';

export default {
  name: 'Notes',
  components: {
    ModalExerciseBlocker,
    ModalConfirmDialog,
    Pdf,
    TinyMCE,
    ModalConfirmDelete,
    InkNote
  },
  data: () => ({
    penInkFormat: 'html',
    currentNote: {},
    displayManual: false,
    deleteDialog: {
      show: false,
      item: null,
      question: null
    },
    confirmDialog: {
      show: false,
      item: null
    }
  }),
  computed: {
    ...mapGetters(['isExerciseBlocked']),
    ...mapState({
      user: (state) => state.user,
      currentExerciseId: (state) => state.run.currentExerciseId,
      currentExerciseDocuments: (state) => {
        const docs = state.exerciseDocuments.exerciseDocuments.find((exDoc) => exDoc.exerciseId === state.run.currentExerciseId).exerciseDocuments
        return docs
      },
      exerciseDocumentsLoaded: (state) => state.exerciseDocuments.loadingState.documents === LoadingState.LOADED,
      exercises: (state) => state.run.exercises,
      notes: (state) => state.notes.notes,
      timetable: (state) => state.run.timetable
    }),
    getExercise() {
      return this.exercises.find((x) => x.id === this.currentExerciseId) || {}
    },
    filteredTimetable() {
      return this.timetable.filter( (t) => t.exerciseId === this.currentExerciseId);
    },
    getTimeUntilEnd() {
      const timetable = this.filteredTimetable;
      return dayjs(timetable[timetable.length - 1].endTime).format('HH:mm - DD.MM.YYYY')
    },
    getTimeUntil() {
      const timetable = this.filteredTimetable;
      const self = this;
      if (Array.isArray(timetable) && timetable.length > 0) {
        return `${self.$i18n.t('NotesPage.timeUntil')} ${self.getTimeUntilEnd}`
      }
      return ''
    }
  },
  methods: {
    reloadInfo() {
      const self = this;
      self.$api.run.getInfo(['candidates'])
    },
    penInkFormatChanged(value) {
      Object.assign(this.confirmDialog, { show: true, item: value })
    },
    dialogConfirmed(item) {
      this.currentNote.penInkFormat = item
      this.currentNote.html = ''
    },
    onTextChange(value) {
      if (!this.currentNote) return;
      this.currentNote.modified = true;
      let update = { ...this.currentNote }
      this.$store.dispatch(ACTIONS.UPDATE_NOTE, update).then( () => {
        this.currentNote.modified = false
      } );
    },
    onTitleChange(value) {
      if (!this.currentNote) return;
      let update = { ...this.currentNote, name: value }
      this.currentNote.modified = true;
      this.$store.dispatch(ACTIONS.UPDATE_NOTE, update).then( () => {
        this.currentNote.modified = false
      });
    },
    persistNote() {
      if (!this.currentNote) return;
      this.currentNote.modified = true;
      let update = { ...this.currentNote };
      this.$store.dispatch(ACTIONS.UPDATE_NOTE, update).then( () => {
        this.currentNote.modified = false
      });
    },
    getDocumentKey(item, prefix) {
      return `${prefix}${item.documentName}`
    },
    getExerciseUrl(item) {
      return item.documentUrl
    },
    setNote(note) {
      this.currentNote = note;
    },
    getCurrentNotes() {
      let result = this.notes.filter( (note) => note != null && note.exerciseId === this.currentExerciseId);

      if (!this.currentNote || !result.includes(this.currentNote)) {
        this.currentNote = result.find( (x) => true);
        if (!this.currentNote) this.addNewNote();
      }
      return result
    },
    addNewNote() {
      let format = 'html';
      if (this.currentNote && this.currentNote.penInkFormat) format = this.currentNote.penInkFormat;
      this.setNote(new BusinessLogicExercise(this).addNewNote(format));
    },
    confirmDeleteNote(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.show = true;
      this.deleteDialog.question = item.name;
    },
    deleteNoteConfirmed(item) {
      new BusinessLogicExercise(this).deleteNote(item)
    }
  }
}
</script>

<style scoped lang='scss'>
  @import '@/ci.scss';

  .isHighlighted {
    background-color: $highlighted;
  }
  .kim-background {
    background-image: url('/background.svg');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: top 0px right -25px;
    background-size: 55%;
  }

</style>
